/**
 * @fileoverview Generic clickable summary/preview card shell.
 * @description The header (icon badge, title, chevron), the big number + caption row, and the
 * footer legend row are identical across every card that uses this shell; only the middle
 * "visual" (a segmented progress bar, a mini bar chart, a 3-column stat grid, or nothing) differs
 * per card, so it's passed in as children rather than each card rebuilding this chrome.
 * Originally built for the sales Dashboard's 6 page-preview cards but has no sales-specific
 * coupling, so it lives here as a shared molecule — any module's "clickable card that links to
 * another page and previews its headline number" (e.g. a hub/landing page's link cards) reuses it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useNavigate } from 'react-router'

import type { ReactNode } from 'react'

interface SummaryCardShellProps {
  icon: ReactNode
  iconBg: string
  title: string
  value: string | number
  caption: string
  valueColor?: string
  to: string
  testId?: string | undefined
  footer?: ReactNode
  children?: ReactNode
}

/**
 * @component
 * @param {SummaryCardShellProps} props - The card's icon/title/value/caption, its destination
 * route, and the middle visual + footer content.
 * @param {ReactNode} props.icon - The small icon rendered in the header badge.
 * @param {string} props.iconBg - The header badge's background color class.
 * @param {string} props.title - The card's title.
 * @param {string | number} props.value - The large headline number.
 * @param {string} props.caption - The caption next to the headline number.
 * @param {string} [props.valueColor] - Text color class for the headline number. Defaults to ink.
 * @param {string} props.to - The route this card navigates to when clicked.
 * @param {string} [props.testId] - `data-testid` of the card, so QA can find each card; its title,
 * value and caption get `-title`, `-value` and `-caption` appended.
 * @param {ReactNode} [props.footer] - The bottom legend row.
 * @param {ReactNode} [props.children] - The card's middle visual (progress bar, chart, or stats).
 */
function SummaryCardShell({
  icon,
  iconBg,
  title,
  value,
  caption,
  valueColor = 'text-ink',
  to,
  testId,
  footer,
  children,
}: SummaryCardShellProps) {
  const navigate = useNavigate()

  /**
   * @description Opens the page the card previews.
   * @returns {void}
   */
  const open = () => void navigate(to)

  return (
    <button
      type="button"
      onClick={open}
      data-testid={testId}
      className="flex cursor-pointer flex-col gap-3.5 rounded-[10px] border border-divider bg-white p-4 pb-4.5 text-left transition-[border-color,box-shadow] hover:border-primary hover:shadow-[0_8px_22px_rgba(15,44,87,0.09)] sm:p-[18px]"
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg ${iconBg}`}
        >
          {icon}
        </div>
        <span
          className="flex-1 text-[14.5px] font-bold text-ink"
          data-testid={testId ? `${testId}-title` : undefined}
        >
          {title}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          className="text-input-border"
          aria-hidden="true"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </div>

      <div className="flex items-baseline gap-2">
        <span
          className={`text-[28px] font-bold leading-none tracking-[-0.8px] sm:text-[30px] ${valueColor}`}
          data-testid={testId ? `${testId}-value` : undefined}
        >
          {value}
        </span>
        <span
          className="text-[13px] font-medium text-slate"
          data-testid={testId ? `${testId}-caption` : undefined}
        >
          {caption}
        </span>
      </div>

      {children}

      {footer && <div className="flex flex-wrap items-center gap-2 gap-y-2">{footer}</div>}
    </button>
  )
}

export default SummaryCardShell
